/**
 * BONJIL — Quiz
 * Open/close: pure JS class toggle on #quiz-modal (moved to body to escape GSAP transforms).
 * Step navigation: Alpine store ($store.quiz).
 */

(function () {
  "use strict";

  const STORAGE_KEY = "bonjil.quiz.v1";
  const CHANNEL_KEY = "bonjil.quiz.channel";
  const UTM_KEY = "bonjil.quiz.utm";
  const TOTAL_STEPS = 23;

  const SECTIONS = [
    { index: 1, label: "Your Stress", from: 1, to: 5 },
    { index: 2, label: "Your Sleep", from: 6, to: 8 },
    { index: 3, label: "Your Skin Under Stress", from: 9, to: 13 },
    { index: 4, label: "Your Ritual", from: 14, to: 22 },
    { index: 5, label: "Closing", from: 23, to: 23 },
  ];

  function getSectionForStep(step) {
    return SECTIONS.find((s) => step >= s.from && step <= s.to) || SECTIONS[0];
  }

  // ── Channel (online vs in-store vs contest) ─────────────────
  // The in-store iPad opens the quiz once via a seed link carrying
  // ?quiz_channel=in-store; contest links (IG bio, paid, PR) carry
  // ?quiz_channel=contest. We persist a sticky flag so later visits on
  // the same device still count for that channel after the URL drops
  // the param.
  function getQuizChannel() {
    try {
      const stored = localStorage.getItem(CHANNEL_KEY);
      return stored === "in-store" || stored === "contest"
        ? stored
        : "online";
    } catch (e) {
      return "online";
    }
  }

  function captureChannel() {
    try {
      const params = new URLSearchParams(window.location.search);
      const explicit = (params.get("quiz_channel") || "").toLowerCase();
      if (
        explicit === "in-store" ||
        explicit === "contest" ||
        explicit === "online"
      ) {
        localStorage.setItem(CHANNEL_KEY, explicit);
        return;
      }
      // Ad/bio links sometimes carry only UTMs (no quiz_channel) — a
      // utm_campaign containing "contest" counts as the contest channel.
      const campaign = (params.get("utm_campaign") || "").toLowerCase();
      if (campaign.indexOf("contest") !== -1) {
        localStorage.setItem(CHANNEL_KEY, "contest");
      }
      // else: no signal in this URL → keep the stored value (sticky)
    } catch (e) {}
  }

  // ── UTM attribution ──────────────────────────────────────────
  // Contest links carry utm_* params that differ per source (bio vs
  // paid vs PR/editor). We persist the latest values so the Klaviyo
  // profile can attribute the opt-in even when the quiz is completed
  // on a later page view.
  function getStoredUtm() {
    try {
      return JSON.parse(localStorage.getItem(UTM_KEY) || "{}") || {};
    } catch (e) {
      return {};
    }
  }

  function captureUtm() {
    try {
      const params = new URLSearchParams(window.location.search);
      const stored = getStoredUtm();
      let changed = false;
      ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach(
        function (k) {
          const v = params.get(k);
          if (v) {
            stored[k] = v;
            changed = true;
          }
        },
      );
      if (changed) localStorage.setItem(UTM_KEY, JSON.stringify(stored));
    } catch (e) {}
  }

  // ── Image preloader ──────────────────────────────────────────
  let _preloaded = false;
  function preloadImages() {
    if (_preloaded) return;
    _preloaded = true;
    const images = window.BONJIL_QUIZ_IMAGES;
    if (!images) return;
    Object.keys(images).forEach(function (key) {
      const src = images[key];
      if (src) {
        new Image().src = src;
      }
    });
  }

  // ── Media crossfade ──────────────────────────────────────────
  function quizScrollTop() {
    const modal = document.querySelector(".quiz-modal.quiz--open");
    if (modal) modal.scrollTop = 0;
  }

  function setMediaImage(step, animate) {
    const images = window.BONJIL_QUIZ_IMAGES;
    if (!images) return;
    const img = document.getElementById("quiz-media-img");
    if (!img) return;
    const src = images[step] || images[1];
    if (!src || img.getAttribute("src") === src) return;

    function swap() {
      img.src = src;
      if (img.complete && img.naturalWidth > 0) {
        img.style.opacity = "1";
      } else {
        img.addEventListener("load", function onLoad() {
          img.removeEventListener("load", onLoad);
          img.style.opacity = "1";
        });
      }
    }

    if (!animate) {
      swap();
      return;
    }

    img.addEventListener(
      "transitionend",
      function handler() {
        img.removeEventListener("transitionend", handler);
        swap();
      },
      { once: true },
    );
    img.style.opacity = "0";
  }

  // ── DOM helpers ──────────────────────────────────────────────
  function getModal() {
    return document.getElementById("quiz-modal");
  }

  function showModal() {
    const el = getModal();
    if (!el) return;
    el.classList.add("quiz--open");
    document.body.style.overflow = "hidden";
  }

  function hideModal() {
    const el = getModal();
    if (!el) return;
    el.classList.remove("quiz--open");
    document.body.style.overflow = "";
  }

  // Global helpers called by inline onclick in Liquid
  window.__quizClose = function () {
    Alpine.store("quiz").isOpen = false;
    hideModal();
  };

  // ── Quiz email body builder ───────────────────────────────────
  const COLLECTION_NAMES = {
    perfection_eternelle: "Perfection Éternelle",
    la_signature_caviar: "La Signature Au Caviar",
    luxe_hydratation: "Luxe Hydratation Absolue",
    ranimer_leclat: "Ranimer L'Éclat",
  };

  function buildQuizEmailBody(answers, email, phone, rec, now, extra) {
    const lines = [];

    lines.push("BONJIL QUIZ — " + now);
    lines.push("");

    lines.push("RECOMMENDATION");
    lines.push("--------------");
    if (rec) {
      const heroName = rec.hero
        ? COLLECTION_NAMES[rec.hero.collection] || rec.hero.collection
        : "-";
      const tierName = rec.tier
        ? rec.tier.tier + " — " + rec.tier.description
        : "-";
      lines.push("Hero Serum:  " + heroName);
      lines.push("Alcove Tier: " + tierName);
      lines.push("Box Color:   " + (rec.color || "-"));
      lines.push("Melody:      " + (rec.melody || "-"));
    } else {
      lines.push("(unavailable)");
    }
    lines.push("");

    lines.push("CUSTOMER");
    lines.push("--------");
    const fullName = (
      (answers.q1_first_name || "") +
      " " +
      (answers.q1_last_name || "")
    ).trim();
    lines.push("Name:   " + (fullName || "-"));
    lines.push("Email:  " + (email || "-"));
    lines.push("Phone:  " + (phone || "-"));
    if (extra && extra.birthday) {
      lines.push("Birthday: " + extra.birthday.label);
    }
    if (extra && extra.igHandle) {
      lines.push("Instagram: @" + extra.igHandle);
    }
    const channelLabel =
      { "in-store": "IN-STORE", contest: "CONTEST" }[getQuizChannel()] ||
      "Online";
    lines.push("Channel: " + channelLabel);
    lines.push("");

    lines.push("STRESS PROFILE");
    lines.push("--------------");
    lines.push("Stress level:   " + (answers.q2_stress_level || "-") + "/10");
    const locs = answers.q3_body_locations;
    lines.push(
      "Body locations: " + (locs && locs.length ? locs.join(", ") : "-"),
    );
    lines.push("Skin symptom:   " + (answers.q12_skin_symptom || "-"));
    lines.push("");

    lines.push("ALL ANSWERS");
    lines.push("-----------");
    const questions = window.BONJIL_QUIZ && window.BONJIL_QUIZ.questions;
    if (questions) {
      questions.forEach(function (q) {
        const val = answers[q.id];
        let display;
        if (val === undefined || val === null || val === "") {
          display = "-";
        } else if (Array.isArray(val)) {
          display = val.length ? val.join(", ") : "-";
        } else {
          display = String(val);
        }
        const pad = q.num < 10 ? " " : "";
        lines.push(pad + q.num + ". " + q.title);
        lines.push("    -> " + display);
      });
    }
    lines.push("");
    lines.push("Completed: " + now);

    return lines.join("\n");
  }

  // ── Contact extras (contest IG handle + birthday) ────────────
  // Accepts "@name", "name", or a pasted profile URL; returns the bare
  // lowercase handle ("" when empty).
  function normalizeIgHandle(raw) {
    if (!raw) return "";
    let h = String(raw).trim().toLowerCase();
    h = h.replace(/^(https?:\/\/)?(www\.)?instagram\.com\//, "");
    h = h.replace(/^@+/, "");
    h = h.replace(/[/?#].*$/, "");
    return h;
  }

  function isValidIgHandle(handle) {
    return /^[a-z0-9._]{1,30}$/.test(handle);
  }

  // Birthday is month/day only (no year — less friction, no age data).
  // Klaviyo's yearly date-trigger flows key off month/day, so the date
  // property uses the obviously-placeholder leap year 1904 (keeps
  // Feb 29 valid and can't be misread as an age).
  function buildBirthday(month, day) {
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    if (!m || !d || m < 1 || m > 12 || d < 1) return null;
    if (d > new Date(1904, m, 0).getDate()) return null;
    const mm = (m < 10 ? "0" : "") + m;
    const dd = (d < 10 ? "0" : "") + d;
    return { iso: "1904-" + mm + "-" + dd, label: mm + "/" + dd };
  }

  // UTM props shared by the lead identify and the "Quiz Started" event.
  function getUtmProps() {
    const utm = getStoredUtm();
    const props = {};
    if (utm.utm_source) props["Quiz UTM Source"] = utm.utm_source;
    if (utm.utm_medium) props["Quiz UTM Medium"] = utm.utm_medium;
    if (utm.utm_campaign) props["Quiz UTM Campaign"] = utm.utm_campaign;
    if (utm.utm_content) props["Quiz UTM Content"] = utm.utm_content;
    return props;
  }

  // ── Klaviyo identify (lead, fired on Q1 continue) ────────────
  // Fires before any real answers exist. Props are a strict whitelist —
  // "Quiz Completed At" (and the "Quiz Completed" event) must NEVER be
  // sent here: completion flows would fire for people who haven't
  // finished the quiz.
  function identifyAtLead(email, phone, answers) {
    if (!window._learnq || !email) return;

    const props = { $email: email };
    if (phone) props["$phone_number"] = phone;
    if (answers.q1_first_name) props["$first_name"] = answers.q1_first_name;
    if (answers.q1_last_name) props["$last_name"] = answers.q1_last_name;
    if (answers.q1_age) props["Quiz Age"] = answers.q1_age;
    props["Quiz Started At"] = new Date().toISOString();
    props["Quiz Channel"] = getQuizChannel();
    Object.assign(props, getUtmProps());

    window._learnq.push(["identify", props]);
  }

  // Recommendation props shared by the completion identify and the
  // "Quiz Completed" event.
  function buildRecProps(rec) {
    if (!rec) return {};
    return {
      "Quiz Hero Serum": rec.hero
        ? COLLECTION_NAMES[rec.hero.collection] || rec.hero.collection
        : "",
      "Quiz Alcove Tier": rec.tier ? rec.tier.tier : "",
      "Quiz Box Color": rec.color || "",
      "Quiz Melody": rec.melody || "",
    };
  }

  function buildResultUrl(answers) {
    const q = new URLSearchParams({
      q12: answers.q12_skin_symptom || "",
      q17: answers.q17_exhale_vision || "",
      q20: answers.q20_safe_color || "",
      q22: answers.q22_stress_sound || "",
      q2: answers.q2_stress_level || "",
      q3: (answers.q3_body_locations || []).join(","),
      q6: answers.q6_sleep_quality || "",
      name: answers.q1_first_name || "",
    });
    return "https://bonjil.com/pages/quiz-results?" + q.toString();
  }

  // Labeled answer props shared by the completion identify and the
  // "Quiz Completed" event. Internal alerts render results from the
  // event, so it must carry the full result set — not just the rec.
  function buildAnswerProps(answers) {
    const labelMap = {};
    const qs = window.BONJIL_QUIZ && window.BONJIL_QUIZ.questions;
    if (qs) {
      qs.forEach(function (q) {
        if (!q.options) return;
        labelMap[q.id] = {};
        q.options.forEach(function (o) {
          labelMap[q.id][o.value] = o.label;
        });
      });
    }
    function getLabel(qid, val) {
      return (labelMap[qid] && labelMap[qid][val]) || val || "";
    }
    function getLabelList(qid, vals) {
      return (vals || [])
        .map(function (v) {
          return getLabel(qid, v);
        })
        .join(", ");
    }

    return {
      "Quiz Stress Level": answers.q2_stress_level || "",
      "Quiz Body Locations": getLabelList(
        "q3_body_locations",
        answers.q3_body_locations,
      ),
      "Quiz Skin Symptom": getLabel(
        "q12_skin_symptom",
        answers.q12_skin_symptom,
      ),
      "Quiz Exhale Vision": getLabel(
        "q17_exhale_vision",
        answers.q17_exhale_vision,
      ),
      "Quiz Safe Color": getLabel("q20_safe_color", answers.q20_safe_color),
      "Quiz Stress Sound": getLabel(
        "q22_stress_sound",
        answers.q22_stress_sound,
      ),
      "Quiz For Whom": getLabel("q23_for_whom", answers.q23_for_whom),
    };
  }

  // ── Klaviyo identify (completion) ────────────────────────────
  function identifyInKlaviyo(answers, email, phone, rec, extra) {
    if (!window._learnq || !email) return;

    const props = { $email: email };
    if (phone) props["$phone_number"] = phone;

    if (answers.q1_first_name) props["$first_name"] = answers.q1_first_name;
    if (answers.q1_last_name) props["$last_name"] = answers.q1_last_name;
    if (answers.q1_age) props["Quiz Age"] = answers.q1_age;

    Object.assign(props, buildRecProps(rec));
    Object.assign(props, buildAnswerProps(answers));
    props["Quiz Completed At"] = new Date().toISOString();
    props["Quiz Version"] = 1;

    props["Quiz Channel"] = getQuizChannel();

    if (extra && extra.igHandle) props["Instagram Handle"] = extra.igHandle;
    if (extra && extra.birthday) {
      props["Birthday"] = extra.birthday.iso;
      props["Birthday (MM/DD)"] = extra.birthday.label;
    }

    // UTM props are deliberately NOT sent here. They were set once by
    // identifyAtLead when the profile was created at Q1 — re-sending the
    // current localStorage values would let an abandon-email link's UTMs
    // overwrite the original acquisition attribution on completion.

    props["Quiz Result URL"] = buildResultUrl(answers);

    window._learnq.push(["identify", props]);
  }

  // ── Klaviyo subscribe (consent-aware) ────────────────────────
  // _learnq.identify only sets profile properties — it does NOT set
  // marketing consent, so quiz takers came through as Never Subscribed.
  // This call hits Klaviyo's Client Subscriptions API with explicit
  // SUBSCRIBED/UNSUBSCRIBED per the SMS/Email checkboxes.
  async function subscribeInKlaviyo(email, phone, smsConsent, emailConsent) {
    const cfg = window.BONJIL_QUIZ_KLAVIYO;
    if (!cfg || !cfg.publicKey || !cfg.listId || !email) return;

    let formattedPhone = "";
    if (phone) {
      const digits = phone.replace(/\D/g, "");
      if (phone.trim().charAt(0) === "+") {
        formattedPhone = "+" + digits;
      } else if (digits.length === 10) {
        formattedPhone = "+1" + digits;
      } else if (digits.length === 11 && digits.charAt(0) === "1") {
        formattedPhone = "+" + digits;
      }
    }

    // The subscribe endpoint only accepts "SUBSCRIBED" — for an unchecked
    // box we omit that channel entirely instead of sending a consent value.
    const subscriptions = {};
    if (emailConsent) {
      subscriptions.email = { marketing: { consent: "SUBSCRIBED" } };
    }
    if (formattedPhone && smsConsent) {
      subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
    }
    // Nothing the user consented to → nothing to subscribe.
    if (!subscriptions.email && !subscriptions.sms) return;

    const profileAttrs = { email: email, subscriptions: subscriptions };
    if (formattedPhone) profileAttrs.phone_number = formattedPhone;

    const body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source:
            {
              "in-store": "Skin Quiz — In-Store",
              contest: "Skin Quiz — Contest",
            }[getQuizChannel()] || "Skin Quiz — Online",
          profile: {
            data: { type: "profile", attributes: profileAttrs },
          },
        },
        relationships: {
          list: { data: { type: "list", id: cfg.listId } },
        },
      },
    };

    try {
      const res = await fetch(
        "https://a.klaviyo.com/client/subscriptions/?company_id=" +
          encodeURIComponent(cfg.publicKey),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: "2026-04-15",
          },
          body: JSON.stringify(body),
          keepalive: true,
        },
      );
      if (!res.ok) {
        console.warn("[quiz] Klaviyo subscribe returned " + res.status);
      }
    } catch (e) {
      console.warn("[quiz] Klaviyo subscribe failed:", e);
    }
  }

  // ── Klaviyo events (Client Events API) ───────────────────────
  // Klaviyo flows can't trigger on profile-property updates, so quiz
  // milestones are also sent as real metrics ("Quiz Started" /
  // "Quiz Completed"). The payload carries the email itself, so shared
  // in-store iPads attribute correctly regardless of cookie identity,
  // and repeat takers log one event per run.
  async function trackQuizEvent(metricName, email, props, profileExtra) {
    const cfg = window.BONJIL_QUIZ_KLAVIYO;
    if (!cfg || !cfg.publicKey || !email) return;

    const body = {
      data: {
        type: "event",
        attributes: {
          properties: props || {},
          time: new Date().toISOString(),
          metric: {
            data: { type: "metric", attributes: { name: metricName } },
          },
          profile: {
            data: {
              type: "profile",
              attributes: Object.assign({ email: email }, profileExtra || {}),
            },
          },
        },
      },
    };

    try {
      const res = await fetch(
        "https://a.klaviyo.com/client/events/?company_id=" +
          encodeURIComponent(cfg.publicKey),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: "2026-04-15",
          },
          body: JSON.stringify(body),
          keepalive: true,
        },
      );
      if (!res.ok) {
        console.warn("[quiz] Klaviyo event returned " + res.status);
      }
    } catch (e) {
      console.warn("[quiz] Klaviyo event failed:", e);
    }
  }

  // ── Alpine store ─────────────────────────────────────────────
  function registerStore() {
    Alpine.store("quiz", {
      isOpen: false,
      isAtWelcome: true,
      currentStep: 1,
      answers: {},
      submitted: false,
      startedAt: null,
      // Contact lives on Q1 alongside name/age so the lead is captured
      // the moment the first step is passed — even on a mid-quiz drop-off.
      contact: { email: "", phone: "", smsConsent: true, emailConsent: true },
      // Last email pushed to Klaviyo at Q1 — skips re-sends when the user
      // back-navigates through Q1 without changing their email.
      leadSentFor: "",
      // Set when a pre-deploy session (answers but no contact) is resumed:
      // Q1 is replayed to collect contact, then we jump back here.
      _resumeStep: null,

      // Contest entries (IG giveaway) must include an Instagram handle
      // so winners can be cross-checked against the follow/like/tag
      // steps. Gated by the section setting + the contest channel flag.
      get contestMode() {
        const cfg = window.BONJIL_QUIZ_CONTEST;
        return !!(cfg && cfg.igFieldEnabled) && getQuizChannel() === "contest";
      },
      igHandleValid: function (raw) {
        return isValidIgHandle(normalizeIgHandle(raw));
      },

      get progressPct() {
        const step = Math.min(this.currentStep, TOTAL_STEPS);
        return Math.round(((step - 1) / TOTAL_STEPS) * 100);
      },
      get section() {
        return getSectionForStep(this.currentStep);
      },
      get sectionLabel() {
        const s = this.section;
        return "Section " + s.index + " of 6 • " + s.label;
      },
      get sectionNumLabel() {
        const s = this.section;
        return "Section " + s.index + " of 6";
      },
      get showContactForm() {
        return this.currentStep === 24;
      },

      hasAnswer: function (questionId) {
        if (!questionId) return true;
        const val = this.answers[questionId];
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      },

      open: function () {
        this.restore();
        this.isOpen = true;
        preloadImages();
        if (this.currentStep > 1 && !this.contact.email) {
          // Saved progress from before Q1 collected contact — replay Q1
          // to capture the lead, then jump back to the saved step.
          this._resumeStep = this.currentStep;
          this.currentStep = 1;
        }
        if (this.currentStep > 1) {
          this.isAtWelcome = false;
          showModal();
          setMediaImage(this.currentStep, false);
        } else {
          this.isAtWelcome = true;
          showModal();
          setMediaImage("welcome", false);
        }
      },

      begin: function () {
        this.isAtWelcome = false;
        setMediaImage(this.currentStep, false);
      },

      // Q1 continue — capture the lead the moment name/contact are in,
      // even if the quiz is never finished. Fire-and-forget: Klaviyo
      // must never block quiz progression.
      submitLead: function () {
        const c = this.contact;
        if (!c.email || c.email.indexOf("@") === -1) return;
        if (this.leadSentFor !== c.email) {
          identifyAtLead(c.email, c.phone, this.answers);
          subscribeInKlaviyo(c.email, c.phone, c.smsConsent, c.emailConsent);
          trackQuizEvent(
            "Quiz Started",
            c.email,
            Object.assign({ "Quiz Channel": getQuizChannel() }, getUtmProps()),
          );
          this.leadSentFor = c.email;
        }
        if (this._resumeStep && this._resumeStep > 1) {
          const resume = this._resumeStep;
          this._resumeStep = null;
          quizScrollTop();
          this.currentStep = resume;
          this.persist();
          setMediaImage(this.currentStep, true);
          return;
        }
        this.next(null, "text", false);
      },

      close: function () {
        window.__quizClose();
      },

      exit: function () {
        window.__quizClose();
      },

      next: function (questionId, inputType, required) {
        if (required && !this.hasAnswer(questionId)) return;
        quizScrollTop();
        if (this.currentStep >= TOTAL_STEPS) {
          this.currentStep = 24;
          this.persist();
          setMediaImage("form", true);
          return;
        }
        this.currentStep++;
        this.persist();
        setMediaImage(this.currentStep, true);
      },

      back: function () {
        quizScrollTop();
        if (this.currentStep === 24) {
          this.currentStep = TOTAL_STEPS;
          this.persist();
          setMediaImage(this.currentStep, true);
          return;
        }
        if (this.currentStep <= 1) {
          this.isAtWelcome = true;
          setMediaImage("welcome", true);
          return;
        }
        this.currentStep--;
        this.persist();
        setMediaImage(this.currentStep, true);
      },

      answer: function (questionId, value) {
        if (!questionId) return;
        this.answers[questionId] = value;
        this.persist();
      },

      toggleMulti: function (questionId, value) {
        if (!questionId) return;
        const current = this.answers[questionId] || [];
        const idx = current.indexOf(value);
        this.answers[questionId] =
          idx === -1
            ? current.concat([value])
            : current.filter(function (v) {
                return v !== value;
              });
        this.persist();
      },

      isMultiSelected: function (questionId, value) {
        return (this.answers[questionId] || []).indexOf(value) !== -1;
      },

      persist: function () {
        try {
          const data = {
            version: 1,
            startedAt: this.startedAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            currentStep: this.currentStep,
            answers: this.answers,
            contact: this.contact,
            leadSentFor: this.leadSentFor,
          };
          this.startedAt = data.startedAt;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {}
      },

      restore: function () {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (!raw) return;
          const data = JSON.parse(raw);
          if (data.version === 1) {
            const store = Alpine.store("quiz");
            store.currentStep = data.currentStep || 1;
            store.startedAt = data.startedAt || null;
            const saved = data.answers || {};
            Object.keys(saved).forEach(function (k) {
              store.answers[k] = saved[k];
            });
            if (data.contact && data.contact.email) {
              store.contact.email = data.contact.email;
              store.contact.phone = data.contact.phone || "";
              store.contact.smsConsent = data.contact.smsConsent !== false;
              store.contact.emailConsent = data.contact.emailConsent !== false;
            }
            store.leadSentFor = data.leadSentFor || "";
          }
        } catch (e) {}
      },

      submitting: false,

      // Contact was captured on Q1; the final step only adds the
      // contest IG handle + birthday extras.
      submitQuiz: async function (extras) {
        if (this.submitting) return;
        this.submitting = true;

        const store = this;
        const answers = store.answers;
        const email = store.contact.email;
        const phone = store.contact.phone;
        const smsConsent = store.contact.smsConsent;
        const emailConsent = store.contact.emailConsent;
        const now = new Date().toISOString();

        const extra = {
          igHandle: this.contestMode
            ? normalizeIgHandle(extras && extras.igHandle)
            : "",
          birthday: buildBirthday(
            extras && extras.birthMonth,
            extras && extras.birthDay,
          ),
        };

        // Build recommendation object (also used for sessionStorage + Klaviyo)
        let rec = null;
        try {
          rec = window.BONJIL_QUIZ && window.BONJIL_QUIZ.recommend(answers);
        } catch (e) {}

        identifyInKlaviyo(answers, email, phone, rec, extra);
        subscribeInKlaviyo(email, phone, smsConsent, emailConsent);
        const completedProps = Object.assign(
          {
            "Quiz Channel": getQuizChannel(),
            "Quiz Version": 1,
            "Quiz Result URL": buildResultUrl(answers),
            "Quiz Completed At": now,
            // Empty-string defaults so alert templates render blank instead
            // of "None" when a completion has no UTM (e.g. in-store).
            "Quiz UTM Source": "",
            "Quiz UTM Medium": "",
            "Quiz UTM Campaign": "",
            "Quiz UTM Content": "",
          },
          getUtmProps(),
          buildRecProps(rec),
          buildAnswerProps(answers),
        );
        if (answers.q1_age) completedProps["Quiz Age"] = answers.q1_age;
        if (answers.q1_first_name)
          completedProps["Quiz First Name"] = answers.q1_first_name;
        if (answers.q1_last_name)
          completedProps["Quiz Last Name"] = answers.q1_last_name;
        // first/last name also ride the event's profile block — the event
        // API upserts them, covering guests whose cookie-based identify
        // never lands (the shared-iPad blank-alert case).
        const profileExtra = {};
        if (answers.q1_first_name)
          profileExtra.first_name = answers.q1_first_name;
        if (answers.q1_last_name) profileExtra.last_name = answers.q1_last_name;
        trackQuizEvent("Quiz Completed", email, completedProps, profileExtra);

        const payload = buildQuizEmailBody(answers, email, phone, rec, now, extra);

        const form = document.getElementById("quiz-contact-form");
        if (form) {
          const emailField = document.getElementById("quiz-contact-email");
          const phoneField = document.getElementById("quiz-contact-phone");
          const bodyField = form.querySelector('[name="contact[body]"]');
          const tagsField = document.getElementById("quiz-contact-tags");
          if (emailField) emailField.value = email || "";
          if (phoneField) phoneField.value = phone || "";
          if (bodyField) bodyField.value = payload;
          if (tagsField)
            tagsField.value = "quiz-submission, channel-" + getQuizChannel();

          // Save result to sessionStorage + localStorage before navigating.
          // sessionStorage is lost if the tab is closed/restored or the link opens
          // in a new tab — localStorage acts as a fallback that the result page clears
          // after reading.
          try {
            if (rec) {
              const resultPayload = JSON.stringify({
                rec: rec,
                answers: answers,
              });
              sessionStorage.setItem("bonjil.quiz.result", resultPayload);
              localStorage.setItem("bonjil.quiz.result", resultPayload);
            }
          } catch (e) {}

          // Submit via fetch so we can redirect afterwards
          try {
            const formData = new FormData(form);
            await fetch("/contact", {
              method: "POST",
              headers: { "X-Requested-With": "XMLHttpRequest" },
              body: formData,
            });
          } catch (e) {}
        }

        store.submitted = true;
        try {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.setItem("bonjil.quiz.done", "1");
        } catch (e) {}
        const _q = new URLSearchParams({
          q12: answers.q12_skin_symptom || "",
          q17: answers.q17_exhale_vision || "",
          q20: answers.q20_safe_color || "",
          q22: answers.q22_stress_sound || "",
          q2: answers.q2_stress_level || "",
          q3: (answers.q3_body_locations || []).join(","),
          q6: answers.q6_sleep_quality || "",
          name: answers.q1_first_name || "",
        });
        // In-store iPads get the price-hidden result page; the Klaviyo
        // "Quiz Result URL" above stays on /pages/quiz-results so emailed
        // links still open the full online version.
        const _resultPath =
          getQuizChannel() === "in-store"
            ? "/pages/instore-results"
            : "/pages/quiz-results";
        window.location.href = _resultPath + "?" + _q.toString();
      },
    });

    // Restore saved progress immediately after store creation,
    // before Alpine processes x-init directives in the DOM.
    // Without this, Q2's x-init fires with empty answers → calls persist()
    // → overwrites localStorage with currentStep:1 before open() can restore.
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.version === 1) {
          const store = Alpine.store("quiz");
          store.currentStep = saved.currentStep || 1;
          store.startedAt = saved.startedAt || null;
          const ans = saved.answers || {};
          Object.keys(ans).forEach(function (k) {
            store.answers[k] = ans[k];
          });
          if (saved.contact && saved.contact.email) {
            store.contact.email = saved.contact.email;
            store.contact.phone = saved.contact.phone || "";
            store.contact.smsConsent = saved.contact.smsConsent !== false;
            store.contact.emailConsent = saved.contact.emailConsent !== false;
          }
          store.leadSentFor = saved.leadSentFor || "";
        }
      }
    } catch (e) {}

    // Move #quiz-modal to <body> BEFORE Alpine processes DOM
    // This escapes any GSAP transform containers that break position:fixed
    const modal = document.getElementById("quiz-modal");
    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  // Capture channel + UTM attribution on every page load.
  captureChannel();
  captureUtm();

  // ── Event listeners ──────────────────────────────────────────
  document.addEventListener("click", function (e) {
    const trigger = e.target.closest("[data-quiz-trigger]");
    if (trigger) {
      e.preventDefault();
      Alpine.store("quiz").open();
    }
  });

  document.addEventListener("keydown", function (e) {
    const modal = getModal();
    if (!modal || !modal.classList.contains("quiz--open")) return;
    if (e.key === "Escape") window.__quizClose();
  });

  // Register store before Alpine initializes so $store.quiz exists when DOM is processed
  document.addEventListener("alpine:init", registerStore);

  // Deep link: ?quiz=open auto-opens the quiz once Alpine has processed
  // the DOM. Contest/bio links use this so visitors land directly in the
  // quiz instead of hunting for the header button.
  document.addEventListener("alpine:initialized", function () {
    try {
      const v = (
        new URLSearchParams(window.location.search).get("quiz") || ""
      ).toLowerCase();
      if (v === "open") Alpine.store("quiz").open();
    } catch (e) {}
  });
})();
